import { useEffect, useRef } from "react";
import {
  View,
  Text,
  Pressable,
  Animated,
  Easing,
  type ViewStyle,
} from "react-native";
import { Users, Heart, Clock, Check, X } from "lucide-react-native";
import { CompactActionButton, IconButton } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../../onboarding/theme";
import { useYouScreen } from "../YouScreenContext";
import { usePartnerInvitation } from "@src/packages/patients/hooks/usePartnerInvite";
import { useCarePersons } from "@src/packages/patients/hooks/useCarePersons";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useDismissible } from "@src/packages/homepage/utils/useDismissible";

/**
 * Two overlapping circles — a gentle visual metaphor for partnership.
 * Left circle is filled (you); right circle is dashed when empty, soft fill
 * when pending, full warm fill when linked.
 */
function PartnerVisual({
  pending,
  linked,
  compact = false,
}: {
  pending?: boolean;
  linked?: boolean;
  compact?: boolean;
}) {
  const size = compact ? 30 : 36;
  const overlap = compact ? 8 : 10;
  const iconSize = compact ? 12 : 14;
  const isActive = !!pending || !!linked;
  const accentColor = isActive ? EMBER.colors.warm : EMBER.colors.textMid;
  const softBackground = isActive
    ? "rgba(184, 74, 22, 0.08)"
    : "rgba(42, 37, 34, 0.05)";
  const softBorder = isActive
    ? "rgba(184, 74, 22, 0.2)"
    : "rgba(42, 37, 34, 0.12)";
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        marginBottom: compact ? 10 : 14,
      }}
    >
      {/* You */}
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: softBackground,
          borderWidth: 1.5,
          borderColor: softBorder,
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <Heart size={iconSize} color={accentColor} strokeWidth={1.5} />
      </View>
      {/* Your person */}
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: linked
            ? "rgba(184, 74, 22, 0.12)"
            : pending
              ? "rgba(184, 74, 22, 0.05)"
              : "rgba(255, 255, 255, 0.5)",
          borderWidth: 1.5,
          borderColor: linked
            ? "rgba(184, 74, 22, 0.25)"
            : pending
              ? "rgba(184, 74, 22, 0.15)"
              : "rgba(42, 37, 34, 0.12)",
          borderStyle: linked || pending ? "solid" : "dashed",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: -overlap,
        }}
      >
        <Users
          size={iconSize}
          color={isActive ? EMBER.colors.warm : EMBER.colors.textFaint}
          strokeWidth={1.5}
        />
      </View>
    </View>
  );
}

/** Soft pulsing dot for pending state */
function PulsingDot() {
  const opacity = useRef(new Animated.Value(0.4)).current;

  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.4,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={{
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: EMBER.colors.warm,
        opacity,
      }}
    />
  );
}

export default function PartnerCard({
  compact = false,
}: {
  compact?: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PartnerCard");
  const { dispatch, patient } = useYouScreen();
  const { data: invitation } = usePartnerInvitation();
  const { partnerName: linkedPartnerName } = useCarePersons();
  const { isDismissed, dismiss } = useDismissible("you/partner-invite-card");

  const hasPartner = !!patient?.partnerConnected;
  const isPending = invitation?.status === "pending";
  const partnerName = invitation?.inviteeFirstName;

  // Partner state belongs in the visual/status treatment below, not in a
  // one-off orange card border. Reuse the standard You glass-card surface.
  const cardStyle = {
    ...GLASS_CARD_STYLE,
    overflow: "hidden",
    padding: compact ? 14 : 18,
    ...(compact ? { minHeight: 198 } : null),
  } as ViewStyle;

  // The invite surface is an optional prompt. A patient can still add a
  // partner later from Connections & Sharing, so retain its dismissal across
  // devices instead of treating the card itself as a required step.
  if (isDismissed && !hasPartner && !isPending) {
    return null;
  }

  // ── Linked state ──
  if (hasPartner) {
    return (
      <View style={{ marginBottom: 16 }}>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                linkedPartnerName
                  ? t("you.partner_card.linked_with_name", {
                      params: { name: linkedPartnerName },
                    })
                  : t("you.partner_card.linked_with_partner"),
              ),
              "partner_card_linked",
            );
            dispatch({
              type: "SHOW_DATA_SHARING_MODAL",
              connectionType: "partner",
            });
          }}
          style={({ pressed }) => [
            cardStyle,
            pressed ? { backgroundColor: "rgba(255, 255, 255, 0.42)" } : null,
          ]}
        >
          <PartnerVisual linked />

          <Text
            className="font-dm-sans"
            style={{
              fontSize: 16,
              fontWeight: "600",
              color: EMBER.colors.text,
              letterSpacing: -0.2,
              marginBottom: 4,
            }}
          >
            {linkedPartnerName
              ? t("you.partner_card.linked_with_name", {
                  params: { name: linkedPartnerName },
                })
              : t("you.partner_card.linked_with_partner")}
          </Text>

          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "400",
              color: EMBER.colors.textLight,
              lineHeight: 18,
              marginBottom: 12,
            }}
          >
            {t("you.partner_card.linked_blurb")}
          </Text>

          <View
            style={{
              alignSelf: "flex-start",
              flexDirection: "row",
              alignItems: "center",
              gap: 5,
              paddingVertical: 5,
              paddingHorizontal: 10,
              borderRadius: 6,
              backgroundColor: "rgba(184, 74, 22, 0.07)",
            }}
          >
            <Check size={11} color={EMBER.colors.warm} strokeWidth={2} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "500",
                color: EMBER.colors.warm,
              }}
            >
              {t("you.partner_card.connected")}
            </Text>
          </View>
        </Pressable>
      </View>
    );
  }

  // ── Pending state ──
  if (isPending) {
    return (
      <View style={{ marginBottom: 16 }}>
        <View style={cardStyle}>
          <PartnerVisual pending />

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 16,
                fontWeight: "600",
                color: EMBER.colors.text,
                letterSpacing: -0.2,
              }}
            >
              {partnerName
                ? t("you.partner_card.waiting_for", {
                    params: { name: partnerName },
                  })
                : t("you.partner_card.waiting_for_partner")}
            </Text>
            <PulsingDot />
          </View>

          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "400",
              color: EMBER.colors.textLight,
              lineHeight: 18,
              marginBottom: 12,
            }}
          >
            {partnerName
              ? t("you.partner_card.we_sent_invite", {
                  params: { name: partnerName },
                })
              : t("you.partner_card.we_sent_invite_generic")}
          </Text>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                paddingVertical: 5,
                paddingHorizontal: 10,
                borderRadius: 6,
                backgroundColor: "rgba(184, 74, 22, 0.05)",
              }}
            >
              <Clock size={11} color={EMBER.colors.warm} strokeWidth={1.5} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "500",
                  color: EMBER.colors.warm,
                }}
              >
                {t("you.partner_card.pending")}
              </Text>
            </View>

            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("you.partner_card.resend")),
                  "you.partner_card.resend",
                );
                dispatch({
                  type: "SET_PARTNER_INVITE_NAME",
                  name: partnerName ?? "",
                });
                dispatch({
                  type: "SET_PARTNER_INVITE_PHONE",
                  phone: invitation?.inviteePhone ?? "",
                });
                dispatch({ type: "SHOW_MODAL", modal: "partner-invite" });
              }}
              hitSlop={8}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "500",
                  color: EMBER.colors.textFaint,
                  textDecorationLine: "underline",
                }}
              >
                {t("you.partner_card.resend")}
              </Text>
            </Pressable>

            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("you.partner_card.manage_invitation")),
                  "you.partner_card.manage_invitation",
                );
                dispatch({
                  type: "SHOW_DATA_SHARING_MODAL",
                  connectionType: "partner",
                });
              }}
              hitSlop={8}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "500",
                  color: EMBER.colors.textFaint,
                  textDecorationLine: "underline",
                }}
              >
                {t("you.partner_card.manage_invitation")}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    );
  }

  // ── Default invite state ──
  return (
    <View
      style={[{ marginBottom: 16 }, compact ? { flex: 1, minWidth: 0 } : null]}
    >
      <View style={cardStyle}>
        <View
          style={{
            position: "absolute",
            top: compact ? 8 : 10,
            right: compact ? 8 : 10,
          }}
        >
          <IconButton
            accessibilityLabel={t("you.partner_card.dismiss")}
            onPress={dismiss}
            size="sm"
            surface="soft"
            backgroundColor="rgba(42, 37, 34, 0.05)"
            pressedBackgroundColor="rgba(42, 37, 34, 0.1)"
          >
            <X size={14} color={EMBER.colors.textLight} strokeWidth={1.8} />
          </IconButton>
        </View>

        <PartnerVisual compact={compact} />

        <Text
          className="font-dm-sans"
          style={{
            fontSize: compact ? 15 : 16,
            lineHeight: compact ? 19 : undefined,
            fontWeight: "600",
            color: EMBER.colors.text,
            letterSpacing: -0.2,
            marginBottom: 4,
          }}
          numberOfLines={compact ? 2 : undefined}
        >
          {t("you.partner_card.doing_this_with_someone")}
        </Text>

        <Text
          className="font-dm-sans"
          style={{
            flex: compact ? 1 : undefined,
            fontSize: compact ? 12 : 13,
            fontWeight: "400",
            color: EMBER.colors.textLight,
            lineHeight: compact ? 16 : 18,
            marginBottom: compact ? 12 : 14,
          }}
          numberOfLines={compact ? 3 : undefined}
        >
          {t(
            compact
              ? "you.partner_card.compact_blurb"
              : "you.partner_card.invite_blurb",
          )}
        </Text>

        <CompactActionButton
          label={t("you.partner_card.invite_partner")}
          onPress={() =>
            dispatch({ type: "SHOW_MODAL", modal: "partner-invite" })
          }
          variant="selected"
          leading={
            <Users size={13} color={EMBER.colors.text} strokeWidth={1.9} />
          }
          numberOfLines={1}
          style={{ alignSelf: compact ? "stretch" : "flex-start" }}
          textStyle={compact ? { fontSize: 12 } : undefined}
        />
      </View>
    </View>
  );
}
